import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/Seo";
import NewsGallery from "../components/NewsGallery";
import { fetchNewsBySlug } from "../cms/news";

/** Datum aus YYYY-MM-DD → "22. Juni 2025" */
function formatDatum(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const monate = ["", "Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
  return `${parseInt(d)}. ${monate[parseInt(m)]} ${y}`;
}

export default function NewsDetail() {
  const { slug } = useParams();
  const [artikel, setArtikel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNewsBySlug(slug)
      .then(setArtikel)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-32 space-y-4">
        <div className="bg-dark/5 animate-pulse h-10 w-3/4" />
        <div className="bg-dark/5 animate-pulse h-64 w-full" />
      </div>
    );
  }

  if (!artikel) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-32 text-center">
        <p className="text-2xl font-extrabold uppercase text-primary">Artikel nicht gefunden</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 bg-primary px-6 py-3 font-bold text-white uppercase text-sm hover:bg-accent transition-colors"
        >
          Zurück zu News
        </Link>
      </section>
    );
  }

  const hauptbild = artikel.photoUrls?.[0] || null;
  const weitereBilder = artikel.photoUrls?.slice(1) || [];

  return (
    <>
      <Seo title={artikel.title} description={artikel.text?.slice(0, 160)} path={`/news/${artikel.slug}`} />

      {/* Header */}
      <section className="bg-primary text-white">
        <div className="mx-auto max-w-4xl px-4 py-16">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-semibold uppercase tracking-wide transition-colors mb-6"
          >
            <svg className="h-4 w-4 rotate-180" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
            Alle News
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {artikel.kategorie && (
              <span className="bg-accent text-white text-xs font-bold uppercase tracking-wide px-2 py-1">
                {artikel.kategorie}
              </span>
            )}
            {artikel.opponent && (
              <span className="text-white/70 text-sm font-semibold">
                vs {artikel.opponent}{artikel.score ? ` — ${artikel.score}` : ""}
              </span>
            )}
            <span className="text-white/60 text-sm">{formatDatum(artikel.date)}</span>
            {artikel.autor && (
              <span className="text-white/60 text-sm">— von {artikel.autor}</span>
            )}
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold uppercase leading-tight max-w-3xl">
            {artikel.title}
          </h1>
        </div>
      </section>

      {/* Hauptbild — nicht abgeschnitten */}
      {hauptbild && (
        <div className="mx-auto max-w-4xl px-4 pt-6">
          <img
            src={hauptbild}
            alt={artikel.title}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full object-contain max-h-[620px] bg-dark/5"
          />
        </div>
      )}

      {/* Artikeltext */}
      <article className="mx-auto max-w-3xl px-4 py-14">
        {artikel.text && (
          <div className="space-y-5 text-dark/80 leading-relaxed whitespace-pre-line">
            {artikel.text}
          </div>
        )}

        {/* YouTube-Highlight-Video */}
        {artikel.videoUrl && (
          <div className="mt-10 aspect-video">
            <iframe
              src={artikel.videoUrl}
              title={artikel.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        {/* Bildergalerie (übrige Fotos ausser dem Hauptbild) */}
        <NewsGallery bilder={weitereBilder} />

        {/* Zurück */}
        <div className="mt-14 pt-8 border-t border-black/10">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 bg-primary px-7 py-3 font-bold text-white uppercase tracking-wide text-sm hover:bg-accent transition-colors"
          >
            <svg className="h-4 w-4 rotate-180" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
            Alle News
          </Link>
        </div>
      </article>
    </>
  );
}
